/**
 * Trade-In Wizard Shared State
 * Manages all state, navigation, and logic for the multi-step trade-in wizard.
 * Used by compramos.vue (layout shell) and compramos/step-*.vue (child pages).
 */
import type { Brand, Category, Product, ProductSku, InspectionProperty, PropertyValue, QuoteResult } from '~/composables/useTradeInAPI'
import type { UserAddress } from '~/composables/useAddressAPI'
import type { CreateTradeInOrderRequest } from '~/composables/useTradeInOrderAPI'
import { isApiError } from '~/utils/api'
import { createCatalogEpochs, isUnavailableCatalogError } from '~/utils/tradeInCatalog'
import {
  buildTradeInCreateOperationPayload,
  buildTradeInDeliveryPayload,
  clearStableTradeInOperationKey,
  createTradeInWizardDraft,
  getStableTradeInOperationKey,
  getSafeTradeInRestoreStep,
  parseTradeInWizardDraft,
  type TradeInLocation,
  type TradeInReturnMethod,
  type TradeInShippingOption,
} from '~/utils/tradeInFulfillment'

const STORAGE_KEY = 'midigi_trade_in_wizard'
const TOTAL_STEPS = 5
const epochs = createCatalogEpochs()
const validationState = ref<'validating' | 'valid' | 'retryableError' | 'unavailable'>('validating')
const PENDING_SUBMISSION_KEY = 'midigi_trade_in_pending_create'
const PENDING_OWNER_KEY = 'midigi_trade_in_pending_owner'
let pendingSubmission: { request: CreateTradeInOrderRequest; key: string; userId: string } | null = null

// Singleton state — shared across parent layout and child pages
const isRestoring = ref(false)
const isLoading = ref(false)
const isSubmitting = ref(false)
const wizardLoadError = ref('')
let retryWizardLoadAction: (() => Promise<void>) | null = null

// Step 1: Brand + Category + Product
const brands = ref<Brand[]>([])
const selectedBrand = ref<Brand | null>(null)
const categories = ref<Category[]>([])
const allBrandProducts = ref<Product[]>([])
const selectedCategory = ref<Category | null>(null)
const selectedProduct = ref<Product | null>(null)
const searchQuery = ref('')
const searchResults = ref<Product[]>([])
const isSearching = ref(false)

// Step 2: Device Assessment
const productSkus = ref<ProductSku[]>([])
const step1Properties = ref<InspectionProperty[]>([])
const selectedPropertyValueIds = ref<string[]>([])
const presetValueIds = ref<Set<string>>(new Set())
const excludedValueIds = ref<Set<string>>(new Set())
const skipDetailedInspection = ref(false)
const isRejected = ref(false)
const rejectionMessage = ref('')
const step2Properties = ref<InspectionProperty[]>([])
const step2PropertiesLoaded = ref(false)
const isLoadingStep2Props = ref(false)

// Step 3: Quote
const quoteResult = ref<QuoteResult | null>(null)

// Step 4: Shipping + Payment
const addresses = ref<UserAddress[]>([])
const addressesLoading = ref(false)
const addressesError = ref('')
const selectedShippingOption = ref<TradeInShippingOption>('PRINT_LABEL')
const selectedReturnAddressId = ref('')
const selectedReturnMethod = ref<TradeInReturnMethod | null>(null)
const mailReceivingLocation = ref<TradeInLocation | null>(null)
const mailReceivingLocationLoading = ref(false)
const mailReceivingLocationError = ref('')
const dropOffLocations = ref<TradeInLocation[]>([])
const dropOffLocationsLoading = ref(false)
const dropOffLocationsError = ref('')
const selectedDropOffLocationId = ref('')
const selectedPaymentMethod = ref('BANK_TRANSFER')
const paymentAccount = ref('')
const paymentAccountHolderName = ref('')

// Initialization flags
let initStarted = false          // prevents double initWizard() calls
const initialized = ref(false)   // true after init complete — gates child page rendering

// Watchers registered flag — prevents duplicate watcher registration
let watchersRegistered = false

export function useTradeInWizard() {
  const { t } = useI18n()
  const localePath = useLocalePath()
  const { formatPrice } = useShopUtils()
  const { isLoggedIn, user } = useAuth()
  const router = useRouter()
  const route = useRoute()
  const toast = useDSToast()

  const tradeInAPI = useTradeInAPI()
  const { fetchAddresses } = useAddressAPI()
  const { createOrder, findCreatedOrderByKey } = useTradeInOrderAPI()
  const receivingAPI = useTradeInReceivingAPI()

  function showWizardLoadError(error: unknown, retry: () => Promise<void>) {
    wizardLoadError.value = error instanceof Error && error.message
      ? error.message
      : t('sellPage.wizard.loadError')
    retryWizardLoadAction = retry
  }

  function clearWizardLoadError() {
    wizardLoadError.value = ''
    retryWizardLoadAction = null
  }

  async function retryWizardLoad() {
    const retry = retryWizardLoadAction
    if (!retry) return
    clearWizardLoadError()
    await retry()
  }

  function invalidateRequests() {
    epochs.invalidateAll()
    if (searchTimeout) clearTimeout(searchTimeout)
    isLoading.value = false
    isSearching.value = false
    isLoadingStep2Props.value = false
  }

  function clearAssessment() {
    productSkus.value = []
    step1Properties.value = []
    step2Properties.value = []
    step2PropertiesLoaded.value = false
    selectedPropertyValueIds.value = []
    presetValueIds.value = new Set()
    excludedValueIds.value = new Set()
    skipDetailedInspection.value = false
    isRejected.value = false
    rejectionMessage.value = ''
    quoteResult.value = null
  }

  function unavailableSelection(error: unknown) {
    invalidateRequests()
    isRestoring.value = true
    selectedProduct.value = null
    selectedCategory.value = null
    clearAssessment()
    searchQuery.value = ''
    searchResults.value = []
    isRestoring.value = false
    validationState.value = 'unavailable'
    initialized.value = true
    clearWizardState()
    showWizardLoadError(error, async () => { await initWizard(true) })
    void router.replace(localePath('/compramos'))
  }

  function catalogLoadFailure(error: unknown, retry: () => Promise<void>) {
    if (isUnavailableCatalogError(error)) unavailableSelection(error)
    else {
      validationState.value = 'retryableError'
      showWizardLoadError(error, retry)
    }
  }

  async function resolveSelection(input: { id: string; categoryId?: string }, restoredIds: string[] = [], detailed = false): Promise<boolean> {
    const current = epochs.begin('selection')
    validationState.value = 'validating'
    isLoading.value = true
    try {
      const availableBrands = await tradeInAPI.fetchBrands()
      const product = await tradeInAPI.fetchProduct(input.id)
      const category = await tradeInAPI.fetchCategory(product.categoryCode)
      const brand = availableBrands.find(item => item.id === category.brandId)
      if (!brand || category.id !== product.categoryId) throw Object.assign(new Error(t('catalogAvailability.unavailable')), { errorKey: 'error.tradein.product_not_found' })
      const [cats, products, skus, props1, props2] = await Promise.all([
        tradeInAPI.fetchCategories(brand.code), tradeInAPI.fetchProductsByBrand(brand.code),
        tradeInAPI.fetchProductSkus(product.id), tradeInAPI.fetchProperties(category.code, 1, product.id),
        detailed ? tradeInAPI.fetchProperties(category.code, 2, product.id) : Promise.resolve(null),
      ])
      if (!current()) return false
      // 全部请求验证后再一次提交。同步 watcher 在此期间不重置刚验证的数据。
      isRestoring.value = true
      brands.value = availableBrands
      selectedBrand.value = brand
      selectedCategory.value = category
      selectedProduct.value = product
      categories.value = cats
      allBrandProducts.value = products
      clearAssessment()
      productSkus.value = skus
      step1Properties.value = props1
      step2Properties.value = props2 ?? []
      step2PropertiesLoaded.value = props2 !== null
      const validIds = new Set([...props1, ...(props2 ?? [])].flatMap(prop => prop.values.map(value => value.id)))
      selectedPropertyValueIds.value = input.categoryId === category.id ? restoredIds.filter(id => validIds.has(id)) : []
      recalculateDerivedState()
      isRestoring.value = false
      validationState.value = 'valid'
      if (!restoredIds.length) autoSelectFunctionalDefaults(props1)
      if (!props1.length && props2 === null) await loadDetailedInspection(product)
      return current() && validationState.value === 'valid'
    } catch (error) {
      if (current()) catalogLoadFailure(error, async () => { await resolveSelection(input, restoredIds, detailed) })
      return false
    } finally {
      if (current()) { isRestoring.value = false; isLoading.value = false }
    }
  }

  // ==================== Step from URL ====================

  const currentStep = computed(() => {
    const path = route.path
    const match = path.match(/step-(\d+)/)
    if (match?.[1]) return parseInt(match[1])
    return 1
  })

  // ==================== Computed ====================

  const matchedSku = computed(() => {
    if (!productSkus.value.length) return null
    const skuPropIds = selectedPropertyValueIds.value.filter(id => {
      for (const prop of step1Properties.value) {
        if (prop.isSkuProperty && prop.values.some(v => v.id === id)) return true
      }
      return false
    })
    return productSkus.value.find(sku => {
      if (sku.skuPpvIds.length !== skuPropIds.length) return false
      return skuPropIds.every(id => sku.skuPpvIds.includes(id))
    }) || null
  })

  // 加价模式：累加所有选中属性值的价值
  const estimatedBasePrice = computed(() => {
    let total = 0
    const allProps = [...step1Properties.value, ...step2Properties.value]
    for (const prop of allProps) {
      for (const val of prop.values) {
        if (selectedPropertyValueIds.value.includes(val.id)) {
          total += val.priceAdjustment > 0 ? val.priceAdjustment : 0
        }
      }
    }
    return total
  })

  // 加价模式下无扣减，保留字段用于向下兼容
  const totalDeductions = computed(() => 0)

  const estimatedPrice = computed(() => {
    return Math.max(0, estimatedBasePrice.value)
  })

  const filteredProducts = computed(() => {
    if (!selectedCategory.value) return allBrandProducts.value
    return allBrandProducts.value.filter(p => p.categoryCode === selectedCategory.value!.code)
  })

  const selectedReturnAddress = computed(() =>
    addresses.value.find(address => address.id === selectedReturnAddressId.value) || null,
  )

  const selectedDropOffLocation = computed(() =>
    dropOffLocations.value.find(location => location.id === selectedDropOffLocationId.value) || null,
  )

  const needsReturnAddress = computed(() => selectedReturnMethod.value === 'SHIP_TO_CUSTOMER')

  const hasRequiredPaymentDetails = computed(() => {
    if (!selectedPaymentMethod.value) return false
    if (selectedPaymentMethod.value === 'BANK_TRANSFER') {
      return Boolean(paymentAccount.value.trim() && paymentAccountHolderName.value.trim())
    }
    if (selectedPaymentMethod.value === 'PAYPAL') return Boolean(paymentAccount.value.trim())
    return selectedPaymentMethod.value === 'STORE_CREDIT'
  })

  const hasCompleteDeliverySelection = computed(() => {
    if (selectedShippingOption.value === 'PRINT_LABEL') {
      return Boolean(
        mailReceivingLocation.value
        && (selectedReturnMethod.value !== 'SHIP_TO_CUSTOMER' || selectedReturnAddressId.value),
      )
    }
    if (selectedShippingOption.value === 'DROP_OFF') {
      if (!selectedDropOffLocationId.value) return false
      return selectedReturnMethod.value !== 'SHIP_TO_CUSTOMER' || Boolean(selectedReturnAddressId.value)
    }
    return false
  })

  const canSubmitOrder = computed(() => Boolean(
    isLoggedIn.value
    && validationState.value === 'valid'
    && !isSubmitting.value
    && selectedProduct.value
    && quoteResult.value
    && !quoteResult.value.isRejected
    && !quoteResult.value.isPricingPending
    && hasRequiredPaymentDetails.value
    && hasCompleteDeliverySelection.value,
  ))

  /**
   * Filter SKU property values based on the selected product's actual SKUs.
   * For SKU properties (e.g., storage), only show values that appear in at least
   * one of the product's SKUs. Non-SKU properties are returned as-is.
   */
  const filteredStep1Properties = computed(() => {
    if (!productSkus.value.length) return step1Properties.value
    // Collect all property value IDs referenced by the product's SKUs
    const validSkuValueIds = new Set<string>()
    for (const sku of productSkus.value) {
      for (const id of sku.skuPpvIds) {
        validSkuValueIds.add(id)
      }
    }
    return step1Properties.value.map(prop => {
      if (!prop.isSkuProperty) return prop
      return {
        ...prop,
        values: prop.values.filter(v => validSkuValueIds.has(v.id)),
      }
    }).filter(prop => !prop.isSkuProperty || prop.values.length > 0)
  })

  const allStep1Answered = computed(() => {
    if (!filteredStep1Properties.value.length) return false
    return filteredStep1Properties.value.every(prop =>
      prop.values.some(v => selectedPropertyValueIds.value.includes(v.id))
    )
  })

  const canProceed = computed(() => {
    if (validationState.value !== 'valid' || isLoading.value || isSubmitting.value) return false
    switch (currentStep.value) {
      case 1: return !!selectedProduct.value
      case 2: {
        if (isRejected.value) return false
        if (isLoadingStep2Props.value) return false
        if (!filteredStep1Properties.value.length && !step2PropertiesLoaded.value) return false
        for (const prop of filteredStep1Properties.value) {
          if (!prop.values.some(v => selectedPropertyValueIds.value.includes(v.id))) return false
        }
        if (step2PropertiesLoaded.value) {
          for (const prop of step2Properties.value) {
            if (!prop.values.some(v => selectedPropertyValueIds.value.includes(v.id))) return false
          }
        }
        if (allStep1Answered.value && !step2PropertiesLoaded.value && !skipDetailedInspection.value) return false
        return true
      }
      case 3: return !!quoteResult.value && !quoteResult.value.isRejected && !quoteResult.value.isPricingPending
      case 4: return canSubmitOrder.value
      case 5: return canSubmitOrder.value
      default: return false
    }
  })

  const stepLabels = computed(() => [
    t('sellPage.wizard.selectDevice'),
    t('sellPage.wizard.inspection'),
    t('sellPage.wizard.quote'),
    t('sellPage.wizard.shipping'),
    t('sellPage.wizard.review'),
  ])

  // ==================== Navigation ====================

  async function focusWizard() {
    if (!import.meta.client) return
    await nextTick()
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        const wizard = document.getElementById('trade-in-wizard-progress')
        if (wizard) {
          wizard.focus({ preventScroll: true })
          const top = wizard.getBoundingClientRect().top + window.scrollY - 16
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' })
        }
        resolve()
      })
    })
  }

  async function navigateToStep(step: number) {
    if (step < 1 || step > TOTAL_STEPS) return
    if (step === 1) {
      await router.push(localePath('/compramos'))
    } else {
      await router.push(localePath(`/compramos/step-${step}`))
    }
    if (import.meta.client && selectedBrand.value) saveWizardState()
  }

  async function nextStep() {
    if (currentStep.value === 4 && !isLoggedIn.value) {
      redirectToLogin()
      return
    }
    if (!canProceed.value) return

    // Step 1 → Step 2: auto-skip if both step1 and step2 properties are empty
    if (currentStep.value === 1 && step2PropertiesLoaded.value && !filteredStep1Properties.value.length && !step2Properties.value.length) {
      if (!await loadQuote()) return
      await navigateToStep(3)
      return
    }

    // Step 2 → Calculate quote → Step 3
    if (currentStep.value === 2) {
      if (!await loadQuote()) return
      await navigateToStep(3)
      return
    }

    await navigateToStep(currentStep.value + 1)
  }

  async function prevStep() {
    if (currentStep.value > 1) {
      await navigateToStep(currentStep.value - 1)
    }
  }

  // ==================== Data Loading ====================

  async function loadQuote(): Promise<boolean> {
    if (!selectedProduct.value || validationState.value !== 'valid') return false
    const current = epochs.begin('quote')
    const product = selectedProduct.value
    const values = [...selectedPropertyValueIds.value]
    const fingerprint = JSON.stringify(values)
    isLoading.value = true
    quoteResult.value = null
    clearWizardLoadError()
    try {
      const result = await tradeInAPI.calculateQuote(product.id, values)
      if (!current() || selectedProduct.value?.id !== product.id || JSON.stringify(selectedPropertyValueIds.value) !== fingerprint) return false
      quoteResult.value = result
      return true
    } catch (error) {
      if (current()) {
        if (isUnavailableCatalogError(error)) unavailableSelection(error)
        else {
          showWizardLoadError(error, async () => { await loadQuote() })
          if (isApiError(error) && error.errorKey?.startsWith('error.tradein.') && (error.status ?? 0) < 500) void navigateToStep(2)
        }
      }
      return false
    } finally { if (current()) isLoading.value = false }
  }

  let mailLocationRequestVersion = 0

  async function loadMailReceivingLocation() {
    const requestVersion = ++mailLocationRequestVersion
    mailReceivingLocation.value = null
    mailReceivingLocationError.value = ''
    if (!isLoggedIn.value || selectedShippingOption.value !== 'PRINT_LABEL') return

    mailReceivingLocationLoading.value = true
    try {
      const location = await receivingAPI.fetchMailLocation()
      if (requestVersion === mailLocationRequestVersion) mailReceivingLocation.value = location
    } catch (error) {
      if (requestVersion === mailLocationRequestVersion) {
        mailReceivingLocationError.value = error instanceof Error ? error.message : t('sellPage.shipping.locationResolveError')
      }
    } finally {
      if (requestVersion === mailLocationRequestVersion) mailReceivingLocationLoading.value = false
    }
  }

  async function loadDropOffLocations(force = false) {
    if (!isLoggedIn.value || selectedShippingOption.value !== 'DROP_OFF') return
    if (!force && (dropOffLocationsLoading.value || dropOffLocations.value.length > 0)) return
    dropOffLocationsLoading.value = true
    dropOffLocationsError.value = ''
    try {
      dropOffLocations.value = await receivingAPI.fetchDropOffLocations()
      if (selectedDropOffLocationId.value && !dropOffLocations.value.some(location => location.id === selectedDropOffLocationId.value)) {
        selectedDropOffLocationId.value = ''
      }
    } catch (error) {
      dropOffLocations.value = []
      dropOffLocationsError.value = error instanceof Error ? error.message : t('sellPage.shipping.dropOffLoadError')
    } finally {
      dropOffLocationsLoading.value = false
    }
  }

  async function loadReturnAddresses(): Promise<boolean> {
    if (!isLoggedIn.value) return false
    addressesLoading.value = true
    addressesError.value = ''
    try {
      addresses.value = await fetchAddresses()
      if (selectedReturnAddressId.value && !addresses.value.some(address => address.id === selectedReturnAddressId.value)) {
        selectedReturnAddressId.value = ''
        toast.warning(t('sellPage.shipping.savedAddressUnavailable'))
      }
      return true
    } catch (error) {
      addresses.value = []
      addressesError.value = error instanceof Error && error.message
        ? error.message
        : t('sellPage.shipping.addressLoadError')
      return false
    } finally {
      addressesLoading.value = false
    }
  }

  // ==================== Derived State Recalculation ====================

  /**
   * Recalculate presetValueIds, excludedValueIds, isRejected, and skipDetailedInspection
   * from the current selectedPropertyValueIds. Used after session restore.
   */
  function recalculateDerivedState() {
    const allProps = [...step1Properties.value, ...step2Properties.value]

    // Recalculate rejection
    isRejected.value = false
    rejectionMessage.value = ''
    for (const p of allProps) {
      for (const v of p.values) {
        if (selectedPropertyValueIds.value.includes(v.id) && v.isRejection) {
          isRejected.value = true
          rejectionMessage.value = v.noAdditionalTips || t('sellPage.wizard.deviceRejected')
        }
      }
    }

    // Recalculate skipDetailedInspection
    skipDetailedInspection.value = false
    for (const p of allProps) {
      for (const v of p.values) {
        if (selectedPropertyValueIds.value.includes(v.id) && v.isNoAdditional) {
          skipDetailedInspection.value = true
        }
      }
    }

    // Recalculate presets
    const newPresets = new Set<string>()
    for (const id of selectedPropertyValueIds.value) {
      for (const p of allProps) {
        const v = p.values.find(val => val.id === id)
        if (v?.presetPpvIds?.length) {
          for (const presetId of v.presetPpvIds) {
            if (selectedPropertyValueIds.value.includes(presetId)) {
              newPresets.add(presetId)
            }
          }
        }
      }
    }
    presetValueIds.value = newPresets

    // Recalculate exclusions
    const newExcluded = new Set<string>()
    for (const id of selectedPropertyValueIds.value) {
      for (const p of allProps) {
        const v = p.values.find(val => val.id === id)
        if (v?.exclusionPpvIds?.length) {
          for (const exId of v.exclusionPpvIds) {
            newExcluded.add(exId)
          }
        }
      }
    }
    excludedValueIds.value = newExcluded
  }

  // ==================== Property Value Selection ====================

  function selectPropertyValue(property: InspectionProperty, value: PropertyValue) {
    // Allow switching within the same property even if the value is preset/excluded
    const isSiblingSwitch = property.values.some(v => v.id !== value.id && selectedPropertyValueIds.value.includes(v.id))
    if (!isSiblingSwitch && (presetValueIds.value.has(value.id) || excludedValueIds.value.has(value.id))) return

    const existingIds = property.values.map(v => v.id)
    selectedPropertyValueIds.value = selectedPropertyValueIds.value.filter(id => !existingIds.includes(id))
    selectedPropertyValueIds.value.push(value.id)

    // Recalculate rejection state from all selected values
    isRejected.value = false
    rejectionMessage.value = ''
    const allProps = [...step1Properties.value, ...step2Properties.value]
    for (const p of allProps) {
      for (const v of p.values) {
        if (selectedPropertyValueIds.value.includes(v.id) && v.isRejection) {
          isRejected.value = true
          rejectionMessage.value = v.noAdditionalTips || t('sellPage.wizard.deviceRejected')
        }
      }
    }

    // Recalculate skipDetailedInspection
    skipDetailedInspection.value = false
    for (const p of allProps) {
      for (const v of p.values) {
        if (selectedPropertyValueIds.value.includes(v.id) && v.isNoAdditional) {
          skipDetailedInspection.value = true
        }
      }
    }

    // Recalculate presets from scratch based on all currently selected values
    const newPresets = new Set<string>()
    for (const id of selectedPropertyValueIds.value) {
      for (const p of allProps) {
        const v = p.values.find(val => val.id === id)
        if (v?.presetPpvIds?.length) {
          for (const presetId of v.presetPpvIds) {
            newPresets.add(presetId)
          }
        }
      }
    }
    // Apply presets, but skip values whose property already has a user selection
    const appliedPresets = new Set<string>()
    for (const presetId of newPresets) {
      if (selectedPropertyValueIds.value.includes(presetId)) {
        appliedPresets.add(presetId)
        continue
      }
      const ownerProp = allProps.find(p => p.values.some(v => v.id === presetId))
      if (ownerProp) {
        const ownerValueIds = ownerProp.values.map(v => v.id)
        const alreadyHasSelection = ownerValueIds.some(id => selectedPropertyValueIds.value.includes(id))
        if (alreadyHasSelection) continue // don't override user's choice, don't mark as preset
      }
      selectedPropertyValueIds.value.push(presetId)
      appliedPresets.add(presetId)
    }
    presetValueIds.value = appliedPresets

    // Recalculate exclusions from scratch
    const newExcluded = new Set<string>()
    for (const id of selectedPropertyValueIds.value) {
      for (const p of allProps) {
        const v = p.values.find(val => val.id === id)
        if (v?.exclusionPpvIds?.length) {
          for (const exId of v.exclusionPpvIds) {
            newExcluded.add(exId)
          }
        }
      }
    }
    excludedValueIds.value = newExcluded
    selectedPropertyValueIds.value = selectedPropertyValueIds.value.filter(id => !newExcluded.has(id))

    // Clean presets that were excluded
    for (const exId of newExcluded) {
      appliedPresets.delete(exId)
    }
    presetValueIds.value = appliedPresets
  }

  function isValueSelected(valueId: string): boolean {
    return selectedPropertyValueIds.value.includes(valueId)
  }

  // ==================== Functional Property Multi-Select ====================

  /**
   * For a FUNCTIONAL property with 2 values, get the "no issue" (first) and "issue" (last) values.
   */
  function getFunctionalValues(property: InspectionProperty) {
    const sorted = [...property.values].sort((a, b) => a.sortOrder - b.sortOrder)
    return {
      noIssueValue: sorted[0],
      issueValue: sorted[sorted.length - 1],
    }
  }

  /**
   * Toggle a FUNCTIONAL property between "has issue" and "no issue".
   */
  function toggleFunctionalProperty(property: InspectionProperty) {
    const { noIssueValue, issueValue } = getFunctionalValues(property)
    if (!noIssueValue || !issueValue) return
    const existingIds = property.values.map(v => v.id)
    const hasIssue = selectedPropertyValueIds.value.includes(issueValue.id)

    selectedPropertyValueIds.value = selectedPropertyValueIds.value.filter(id => !existingIds.includes(id))
    selectedPropertyValueIds.value.push(hasIssue ? noIssueValue.id : issueValue.id)
  }

  /**
   * Check if a FUNCTIONAL property currently has "issue" selected.
   */
  function isFunctionalChecked(property: InspectionProperty): boolean {
    const { issueValue } = getFunctionalValues(property)
    return Boolean(issueValue && selectedPropertyValueIds.value.includes(issueValue.id))
  }

  /**
   * Auto-select "no issue" defaults for all FUNCTIONAL properties that have no selection yet.
   */
  function autoSelectFunctionalDefaults(properties: InspectionProperty[]) {
    for (const prop of properties) {
      if (prop.propertyType !== 'FUNCTIONAL') continue
      const existingIds = prop.values.map(v => v.id)
      if (existingIds.some(id => selectedPropertyValueIds.value.includes(id))) continue
      const { noIssueValue } = getFunctionalValues(prop)
      if (noIssueValue) selectedPropertyValueIds.value.push(noIssueValue.id)
    }
  }

  // ==================== Search ====================

  let searchTimeout: ReturnType<typeof setTimeout> | null = null

  async function runProductSearch(query: string) {
    const current = epochs.begin('search')
    isSearching.value = true
    clearWizardLoadError()
    try {
      const results = await tradeInAPI.searchProducts(query)
      if (current()) searchResults.value = results
    } catch (error) {
      if (current()) { searchResults.value = []; showWizardLoadError(error, async () => { await runProductSearch(query) }) }
    } finally { if (current()) isSearching.value = false }
  }

  function onSearchInput() {
    epochs.invalidate('search')
    if (searchTimeout) clearTimeout(searchTimeout)
    searchResults.value = []
    isSearching.value = false
    if (!searchQuery.value.trim()) return
    searchTimeout = setTimeout(async () => { await runProductSearch(searchQuery.value.trim()) }, 300)
  }

  async function selectSearchResult(product: Product) {
    invalidateRequests()
    searchQuery.value = ''
    searchResults.value = []
    if (await resolveSelection(product)) await nextStep()
  }

  // ==================== Order Submission ====================

  function clearPendingMarker() {
    sessionStorage.removeItem(PENDING_SUBMISSION_KEY)
    sessionStorage.removeItem(PENDING_OWNER_KEY)
  }

  function requirePendingOwner(owner: string | null | undefined) {
    if (!owner || owner !== user.value?.id) throw new Error(t('catalogAvailability.pendingAccount'))
  }

  async function finishRejectedSubmission(error: unknown): Promise<boolean> {
    if (!isApiError(error) || (error.status ?? 0) < 400 || (error.status ?? 0) >= 500
        || error.status === 401 || !error.errorKey
        || error.errorKey === 'error.concurrent_lock_timeout' || error.errorKey === 'error.order.idempotency_key_reused'
        || (pendingSubmission && pendingSubmission.userId !== user.value?.id)) return false

    // 明确的创建拒绝结束本次尝试；未知结果仍保留原请求，不能换键重建。
    pendingSubmission = null
    try { clearPendingMarker() } catch { /* ignore */ }
    clearStableTradeInOperationKey('create')
    clearWizardLoadError()
    if (isUnavailableCatalogError(error)) unavailableSelection(error)
    else if (error.errorKey === 'error.concurrent_update') await initWizard(true)
    else if (['error.order.device_rejected', 'error.order.tradein_pricing_pending',
      'error.tradein.no_valid_property_values', 'error.tradein.invalid_property_values',
      'error.tradein.missing_required_properties'].includes(error.errorKey)) {
      quoteResult.value = null
      await navigateToStep(2)
    } else await navigateToStep(4)
    toast.error(error.message)
    return true
  }

  async function submitOrder() {
    if (isSubmitting.value || !isLoggedIn.value || (!pendingSubmission && !canSubmitOrder.value)) return
    if (pendingSubmission || sessionStorage.getItem(PENDING_SUBMISSION_KEY)) { await retryPendingSubmission(); return }
    const product = selectedProduct.value
    const userId = user.value?.id
    if (!product || !userId) return
    isSubmitting.value = true

    try {
      const delivery = buildTradeInDeliveryPayload({
        shippingOption: selectedShippingOption.value,
        dropOffLocationId: selectedDropOffLocationId.value || undefined,
        returnMethod: selectedReturnMethod.value,
        returnAddressId: selectedReturnAddressId.value || undefined,
      })
      const request: CreateTradeInOrderRequest = {
        productId: product.id,
        selectedPropertyValueIds: [...selectedPropertyValueIds.value],
        ...delivery,
        paymentMethod: selectedPaymentMethod.value,
      }

      if (selectedPaymentMethod.value !== 'STORE_CREDIT' && paymentAccount.value.trim()) {
        request.paymentAccount = paymentAccount.value.trim()
      }
      if (selectedPaymentMethod.value === 'BANK_TRANSFER') {
        request.paymentAccountHolderName = paymentAccountHolderName.value.trim()
      }

      if (!pendingSubmission) {
        const key = await getStableTradeInOperationKey('create', buildTradeInCreateOperationPayload(request))
        pendingSubmission = { request: structuredClone(request), key, userId }
        try { sessionStorage.setItem(PENDING_SUBMISSION_KEY, key); sessionStorage.setItem(PENDING_OWNER_KEY, userId) } catch { /* ignore */ }
      }
      requirePendingOwner(pendingSubmission.userId)
      const order = await createOrder(pendingSubmission.request, pendingSubmission.key)
      requirePendingOwner(pendingSubmission.userId)
      pendingSubmission = null
      try { clearPendingMarker() } catch { /* ignore */ }
      clearStableTradeInOperationKey('create')
      clearWizardState()
      toast.success(t('sellPage.orderSuccess'))
      router.push(localePath(`/cuenta/sell-orders/${order.id}`))
    } catch (e) {
      if (!await finishRejectedSubmission(e)) {
        showWizardLoadError(new Error(t('catalogAvailability.uncertainSubmission')), async () => { await retryPendingSubmission() })
        toast.error(e instanceof Error ? e.message : t('sellPage.orderError'))
      }
    } finally {
      isSubmitting.value = false
    }
  }

  async function retryPendingSubmission() {
    if (!isLoggedIn.value || isSubmitting.value) return
    isSubmitting.value = true
    let replayingCreation = false
    try {
      const storedKey = pendingSubmission?.key ?? sessionStorage.getItem(PENDING_SUBMISSION_KEY)
      if (!storedKey) return
      const owner = pendingSubmission?.userId ?? sessionStorage.getItem(PENDING_OWNER_KEY)
      requirePendingOwner(owner)
      // 查询也拿同一用户锁，确保原创建事务已结束；刷新后无须恢复敏感付款账户。
      let order = await findCreatedOrderByKey(storedKey)
      requirePendingOwner(owner)
      if (!order && pendingSubmission) {
        replayingCreation = true
        order = await createOrder(pendingSubmission.request, storedKey)
        requirePendingOwner(owner)
      }
      if (!order && !pendingSubmission) {
        clearPendingMarker()
        clearStableTradeInOperationKey('create')
        clearWizardLoadError()
        await initWizard(true)
        return
      }
      if (order) {
        pendingSubmission = null
        clearPendingMarker()
        clearStableTradeInOperationKey('create')
        clearWizardState()
        await router.push(localePath(`/cuenta/sell-orders/${order.id}`))
      }
    } catch (error) {
      // 查询失败不证明原事务未创建；只有查询确认不存在后的创建拒绝可结束尝试。
      if (!replayingCreation || !await finishRejectedSubmission(error)) showWizardLoadError(error, retryPendingSubmission)
    } finally { isSubmitting.value = false }
  }

  // ==================== Session Persistence ====================

  function saveWizardState() {
    if (validationState.value !== 'valid' || isRestoring.value) return
    const state = createTradeInWizardDraft({
      currentStep: currentStep.value,
      selectedBrand: selectedBrand.value,
      selectedCategory: selectedCategory.value,
      selectedProduct: selectedProduct.value,
      selectedPropertyValueIds: selectedPropertyValueIds.value,
      skipDetailedInspection: skipDetailedInspection.value,
      selectedShippingOption: selectedShippingOption.value,
      selectedReturnAddressId: selectedReturnAddressId.value,
      selectedReturnMethod: selectedReturnMethod.value,
      selectedDropOffLocationId: selectedDropOffLocationId.value,
      selectedPaymentMethod: selectedPaymentMethod.value,
    })
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)) } catch { /* ignore */ }
  }

  function clearWizardState() {
    try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
  }

  async function restoreWizardState(): Promise<number | false> {
    let raw: string | null = null
    try { raw = sessionStorage.getItem(STORAGE_KEY) } catch { return false }
    const state = parseTradeInWizardDraft(raw)
    if (!state?.selectedProduct || typeof state.selectedProduct !== 'object'
        || !('id' in state.selectedProduct) || typeof state.selectedProduct.id !== 'string') return false
    const savedProduct = {
      id: state.selectedProduct.id,
      categoryId: 'categoryId' in state.selectedProduct && typeof state.selectedProduct.categoryId === 'string'
        ? state.selectedProduct.categoryId : undefined,
    }
    const restoredIds = Array.isArray(state.selectedPropertyValueIds) ? state.selectedPropertyValueIds.filter(id => typeof id === 'string') : []
    const valid = await resolveSelection(savedProduct, restoredIds, state.currentStep >= 2)
    if (!valid) {
      if (validationState.value === 'unavailable') return 1
      throw new Error(wizardLoadError.value || t('sellPage.wizard.loadError'))
    }
    isRestoring.value = true
    selectedShippingOption.value = state.selectedShippingOption
    selectedReturnAddressId.value = state.selectedReturnAddressId || ''
    selectedReturnMethod.value = state.selectedReturnMethod ?? null
    selectedDropOffLocationId.value = state.selectedDropOffLocationId || ''
    selectedPaymentMethod.value = state.selectedPaymentMethod || 'BANK_TRANSFER'
    paymentAccount.value = ''
    paymentAccountHolderName.value = ''
    isRestoring.value = false
    let step = getSafeTradeInRestoreStep(state.currentStep)
    if (savedProduct.categoryId !== selectedProduct.value?.categoryId || !canProceedInspection()) step = 2
    if (step >= 3 && !await loadQuote()) step = 2
    if (isLoggedIn.value && step >= 4) await loadReturnAddresses()
    if (selectedShippingOption.value === 'PRINT_LABEL') await loadMailReceivingLocation()
    else if (selectedShippingOption.value === 'DROP_OFF') await loadDropOffLocations()
    return validationState.value === 'unavailable' ? 1 : step
  }

  function canProceedInspection() {
    if (isRejected.value) return false
    const props = [...filteredStep1Properties.value, ...(skipDetailedInspection.value ? [] : step2Properties.value)]
    return props.every(prop => !prop.isRequired || prop.values.some(value => selectedPropertyValueIds.value.includes(value.id)))
  }

  /**
   * Called from TradeInCalculator to pre-fill wizard with selected product and jump to step 2.
   */
  function prefillFromCalculator(brand: Brand, category: Category | null, product: Product) {
    // Save state so initWizard() will restore to step 2
    const state = createTradeInWizardDraft({
      currentStep: 2,
      selectedBrand: brand,
      selectedCategory: category,
      selectedProduct: product,
      selectedPropertyValueIds: [],
      skipDetailedInspection: false,
      selectedShippingOption: 'PRINT_LABEL',
      selectedReturnAddressId: '',
      selectedReturnMethod: null,
      selectedDropOffLocationId: '',
      selectedPaymentMethod: 'BANK_TRANSFER',
    })
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)) } catch { /* ignore */ }

    invalidateRequests()
    validationState.value = 'validating'
    // Reset init flags so the wizard re-initializes on next mount
    initStarted = false
    initialized.value = false

    router.push(localePath('/compramos/step-2'))
  }

  function redirectToLogin() {
    invalidateRequests()
    saveWizardState()
    paymentAccount.value = ''
    paymentAccountHolderName.value = ''
    initStarted = false
    initialized.value = false
    router.push({
      path: localePath('/login'),
      query: { redirect: localePath('/compramos/step-4') },
    })
  }

  // ==================== Reset ====================

  function resetWizard() {
    invalidateRequests()
    validationState.value = 'validating'
    selectedBrand.value = null
    selectedCategory.value = null
    selectedProduct.value = null
    categories.value = []
    allBrandProducts.value = []
    productSkus.value = []
    step1Properties.value = []
    step2Properties.value = []
    selectedPropertyValueIds.value = []
    presetValueIds.value = new Set()
    excludedValueIds.value = new Set()
    skipDetailedInspection.value = false
    step2PropertiesLoaded.value = false
    isRejected.value = false
    rejectionMessage.value = ''
    quoteResult.value = null
    clearWizardLoadError()
    selectedShippingOption.value = 'PRINT_LABEL'
    selectedReturnAddressId.value = ''
    selectedReturnMethod.value = null
    mailReceivingLocation.value = null
    mailReceivingLocationError.value = ''
    addressesError.value = ''
    dropOffLocations.value = []
    dropOffLocationsError.value = ''
    selectedDropOffLocationId.value = ''
    selectedPaymentMethod.value = 'BANK_TRANSFER'
    paymentAccount.value = ''
    paymentAccountHolderName.value = ''
    searchQuery.value = ''
    searchResults.value = []
    clearWizardState()
    router.push(localePath('/compramos'))
  }

  // ==================== Watchers (registered once from parent layout) ====================

  async function loadBrandCatalog(brand: Brand) {
    const current = epochs.begin('directory')
    isLoading.value = true
    clearWizardLoadError()
    try {
      const [cats, products] = await Promise.all([tradeInAPI.fetchCategories(brand.code), tradeInAPI.fetchProductsByBrand(brand.code)])
      if (!current()) return
      categories.value = cats
      allBrandProducts.value = products
    } catch (error) {
      if (current()) showWizardLoadError(error, async () => { await loadBrandCatalog(brand) })
    } finally { if (current()) isLoading.value = false }
  }

  async function loadDetailedInspection(product: Product): Promise<boolean> {
    const current = epochs.begin('properties')
    isLoadingStep2Props.value = true
    clearWizardLoadError()
    try {
      const properties = await tradeInAPI.fetchProperties(product.categoryCode, 2, product.id)
      if (!current()) return false
      step2Properties.value = properties
      autoSelectFunctionalDefaults(properties)
      step2PropertiesLoaded.value = true
      validationState.value = 'valid'
      return true
    } catch (error) {
      if (current()) catalogLoadFailure(error, async () => { await loadDetailedInspection(product) })
      return false
    } finally { if (current()) isLoadingStep2Props.value = false }
  }

  async function loadProductAssessment(product: Product) { await resolveSelection(product) }

  function registerWatchers() {
    if (watchersRegistered) return
    watchersRegistered = true

    watch(selectedBrand, async (brand) => {
      if (isRestoring.value) return
      invalidateRequests()
      validationState.value = 'validating'
      clearAssessment()
      selectedCategory.value = null
      selectedProduct.value = null
      allBrandProducts.value = []
      categories.value = []
      if (brand) {
        await loadBrandCatalog(brand)
      }
    }, { flush: 'sync' })

    watch(selectedCategory, () => {
      if (isRestoring.value) return
      invalidateRequests()
      validationState.value = 'validating'
      clearAssessment()
      selectedProduct.value = null
    }, { flush: 'sync' })

    watch(selectedProduct, async (product) => {
      if (isRestoring.value) return
      invalidateRequests()
      validationState.value = 'validating'
      productSkus.value = []
      step1Properties.value = []
      step2Properties.value = []
      step2PropertiesLoaded.value = false
      selectedPropertyValueIds.value = []
      presetValueIds.value = new Set()
      excludedValueIds.value = new Set()
      skipDetailedInspection.value = false
      isRejected.value = false
      quoteResult.value = null
      if (product) {
        await loadProductAssessment(product)
      }
    }, { flush: 'sync' })

    watch(selectedPropertyValueIds, () => {
      epochs.invalidate('quote')
      quoteResult.value = null
      if (!isRestoring.value && validationState.value === 'valid') isLoading.value = false
    }, { deep: true, flush: 'sync' })

    watch(allStep1Answered, async (answered) => {
      if (isRestoring.value || validationState.value !== 'valid' || !answered || skipDetailedInspection.value || step2PropertiesLoaded.value || isLoadingStep2Props.value || !selectedProduct.value) return
      await loadDetailedInspection(selectedProduct.value)
    })

    watch(selectedShippingOption, async (option, previous) => {
      if (isRestoring.value || option === previous) return
      if (option === 'PRINT_LABEL') {
        selectedReturnMethod.value = null
        selectedReturnAddressId.value = ''
        selectedDropOffLocationId.value = ''
        dropOffLocationsError.value = ''
        await loadMailReceivingLocation()
      } else if (option === 'DROP_OFF') {
        mailLocationRequestVersion++
        mailReceivingLocation.value = null
        mailReceivingLocationError.value = ''
        selectedReturnMethod.value = null
        selectedReturnAddressId.value = ''
        await loadDropOffLocations()
      }
    }, { flush: 'sync' })

    watch(selectedReturnMethod, (method) => {
      if (isRestoring.value) return
      if (selectedShippingOption.value === 'DROP_OFF' && method === 'PICKUP_AT_STORE') {
        selectedReturnAddressId.value = ''
      }
    }, { flush: 'sync' })

    // Load authenticated fulfillment data when entering step 4.
    watch(currentStep, async (step) => {
      if (step !== 4 || !isLoggedIn.value) return
      await Promise.allSettled([
        loadReturnAddresses(),
        selectedShippingOption.value === 'PRINT_LABEL'
          ? loadMailReceivingLocation()
          : loadDropOffLocations(),
      ])
    })

    // Keep browser back/forward navigation aligned with the same operation area
    // as the wizard's next/previous buttons.
    watch(currentStep, async (step, previousStep) => {
      if (previousStep !== undefined && step !== previousStep) await focusWizard()
    })
  }

  // ==================== Initialization ====================

  async function initWizard(force = false) {
    if (!import.meta.client || (initStarted && !force)) return
    initStarted = true
    invalidateRequests()
    const current = epochs.begin('initialization')
    validationState.value = 'validating'
    registerWatchers()
    isLoading.value = true
    clearWizardLoadError()
    try {
      const result = await tradeInAPI.fetchBrands()
      if (!current()) return
      brands.value = result
      const restoredStep = await restoreWizardState()
      if (!current()) return
      initialized.value = true
      if (restoredStep) {
        const target = restoredStep === 1 ? '/compramos' : `/compramos/step-${restoredStep}`
        await router.replace(localePath(target))
      } else {
        await router.replace(localePath('/compramos'))
        selectedBrand.value = brands.value[0] ?? null
      }
      if (sessionStorage.getItem(PENDING_SUBMISSION_KEY)) {
        showWizardLoadError(new Error(t('catalogAvailability.uncertainSubmission')), retryPendingSubmission)
      }
    } catch (error) {
      if (current()) {
        initialized.value = true
        validationState.value = 'retryableError'
        showWizardLoadError(error, async () => { await initWizard(true) })
      }
    } finally { if (current()) isLoading.value = false }
  }

  // ==================== Step Guard ====================

  function guardStep(step: number): boolean {
    if (step === 1) return true
    if (validationState.value !== 'valid') return false
    if (step === 2) return !!selectedProduct.value
    if (step === 3) return !!quoteResult.value
    if (step === 4) return !!quoteResult.value && !quoteResult.value.isRejected && !quoteResult.value.isPricingPending
    if (step === 5) return !!quoteResult.value && !quoteResult.value.isRejected && !quoteResult.value.isPricingPending
    return false
  }

  return {
    // Constants
    totalSteps: TOTAL_STEPS,

    // State
    isRestoring,
    isLoading,
    isSubmitting,
    initialized,
    validationState,
    retryPendingSubmission,
    wizardLoadError,

    // Step 1
    brands,
    selectedBrand,
    categories,
    allBrandProducts,
    selectedCategory,
    selectedProduct,
    searchQuery,
    searchResults,
    isSearching,

    // Step 2
    productSkus,
    step1Properties,
    filteredStep1Properties,
    selectedPropertyValueIds,
    presetValueIds,
    excludedValueIds,
    skipDetailedInspection,
    isRejected,
    rejectionMessage,
    step2Properties,
    step2PropertiesLoaded,
    isLoadingStep2Props,

    // Step 3
    quoteResult,

    // Step 4
    addresses,
    addressesLoading,
    addressesError,
    selectedShippingOption,
    selectedReturnAddressId,
    selectedReturnAddress,
    selectedReturnMethod,
    needsReturnAddress,
    mailReceivingLocation,
    mailReceivingLocationLoading,
    mailReceivingLocationError,
    dropOffLocations,
    dropOffLocationsLoading,
    dropOffLocationsError,
    selectedDropOffLocationId,
    selectedDropOffLocation,
    selectedPaymentMethod,
    paymentAccount,
    paymentAccountHolderName,

    // Computed
    currentStep,
    matchedSku,
    estimatedBasePrice,
    totalDeductions,
    estimatedPrice,
    filteredProducts,
    allStep1Answered,
    canProceed,
    canSubmitOrder,
    stepLabels,

    // Navigation
    navigateToStep,
    focusWizard,
    nextStep,
    prevStep,

    // Actions
    loadQuote,
    selectPropertyValue,
    isValueSelected,
    toggleFunctionalProperty,
    isFunctionalChecked,
    onSearchInput,
    selectSearchResult,
    submitOrder,
    redirectToLogin,
    loadMailReceivingLocation,
    loadDropOffLocations,
    loadReturnAddresses,
    retryWizardLoad,
    resetWizard,
    initWizard,
    guardStep,
    saveWizardState,
    prefillFromCalculator,

    // Utils re-exported for child pages
    formatPrice,
    t,
    localePath,
    isLoggedIn,
  }
}
